import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  LeftArrowIcon,
  HeartIcon,
  LinkIcon,
  DocumentIcon,
  MusicCircleicon,
  LoadMoreButton,
  FavoritesIcon
} from '../components';
import { fetchSingleCatecheticalCourse, fetchCatechesisList, toggleFavorite } from '../utilities/api';
import { mapCatechesisListData, mapSingleCatechesisCourseData } from '../utilities';
import useCopyToClipboard from "../hooks/useCopyToClipboard";
import { Skeleton } from '../components/ui/skeleton';
const SingleLearnTheFaith = () => {
  const { slug, id } = useParams(); // Get Course id from URL
  const { copied, copyToClipboard } = useCopyToClipboard();
  // Course Details State
  const [courseData, setCourseData] = useState(null);
  const [loadingCourse, setLoadingCourse] = useState(true);
  const [errorCourse, setErrorCourse] = useState(null);
  // Catechesis List State
  const [catechesisList, setCatechesisList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorList, setErrorList] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const itemsPerPage = 10;
  const [totalRecords, setTotalRecords] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false); // Tracks when API is fully finished
  const [finalError, setFinalError] = useState(null); // Track final error safely

  useEffect(() => {
    if (catechesisList.length > 0) {
      setDataLoaded(true);  // Mark as loaded once we have data
      setFinalError(null);   // Clear errors if data is available
    } else if (!loadingList) {
      // Only set error if API has finished AND still no data
      setFinalError(errorList);
    }
  }, [catechesisList, loadingList, errorList]);

  // Fetch Course Details
  useEffect(() => {
    const loadCourseData = async () => {
      try {
        setLoadingCourse(true);
        const rawData = await fetchSingleCatecheticalCourse(slug);
        const listResponse = await fetchCatechesisList(id, 0, itemsPerPage);
        const total = listResponse.meta?.pagination?.total || 0;
        const transformed = mapSingleCatechesisCourseData(rawData , total);
        setCourseData(transformed);

        // if (response.count == 0) {
        //   setLoadingList(false);
        // }
      } catch (error) {
        console.error("Error fetching course details:", error);
        setErrorCourse("Failed to load course details.");
      } finally {
        setLoadingCourse(false);
      }
    };
    loadCourseData();
  }, [slug]);

  // Fetch Initial Catechesis List
  useEffect(() => {
    // if (!courseData || courseData.count === 0) {
    //   setCatechesisList([]);
    //   return;
    // }

    if (!courseData) return

    const loadCatechesisList = async () => {
      setLoadingList(true);
      try {
        const response = await fetchCatechesisList(id, 0, itemsPerPage);
        const transformedData = mapCatechesisListData(response.data || []);
        setCatechesisList(transformedData);
        setOffset(itemsPerPage);
        setTotalRecords(response.meta?.pagination?.total || 0);
        setHasMore(response.meta?.pagination?.total > itemsPerPage);
      } catch (error) {
        console.error("Error fetching catechesis list:", error);
        setErrorList("Failed to load catechesis.");
      } finally {
        setLoadingList(false);
      }
    };

    loadCatechesisList();
  }, [id, courseData]);

  // Load More Function
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const response = await fetchCatechesisList(id, offset, itemsPerPage);

      const responseData = response.data || [];
      const transformedData = mapCatechesisListData(responseData);

      setCatechesisList(prev => [...prev, ...transformedData]);
      setOffset(prev => prev + itemsPerPage);
      setHasMore(offset + itemsPerPage < totalRecords);
    } catch (error) {
      console.error("Error loading more catechesis:", error);
      setErrorList("Failed to load more catechesis.");
    } finally {
      setLoadingMore(false);
    }
  };

  const handleToggleFavorite = async (lessonId, event) => {
    if (event) {
      event.preventDefault(); // Prevents page reload
      event.stopPropagation(); // Stops event bubbling
    }

    try {
      const currentFavorite = catechesisList.find(item => item.id === lessonId)?.favorite;

      // Step 1: Ensure prevData is an array before modifying
      setCatechesisList((prevList) =>
        prevList.map((lesson) =>
          lesson.id === lessonId ? { ...lesson, favorite: !lesson.favorite } : lesson
        )
      );


      // Step 3: Call API (Do NOT reload everything)
      await toggleFavorite('catecheses', lessonId, false, currentFavorite);
    } catch (error) {
      console.error("Failed to update favorite:", error);

      // Step 4: If API call fails, revert UI
      setCatechesisList((prevList) =>
        prevList.map((lesson) =>
          lesson.id === lessonId ? { ...lesson, favorite: !lesson.favorite } : lesson
        )
      );

    }
  };

  const handleToggleCourseFavorite = async (event, id) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!id) {
      console.error("Course Id is missing. Cannot toggle Favorite");
      return;
    }

    const currentFavorite = courseData.favorite
    try {

      console.log(`Toggling favorite for Course Id : ${id}`);


      const newFavorite = !courseData.favorite;

      setCourseData(prev => ({
        ...prev,
        favorite: newFavorite,
      }));

      await toggleFavorite('catechetical-courses', id, true, currentFavorite);

      console.log(`Successfully updated favorite for Course id : ${id}`);

    } catch (error) {
      console.error("Failed to update course favorite:", error);

      // Revert on failure
      setCourseData(prev => ({
        ...prev,
        favorite: !prev.favorite
      }));
    }
  };

  return (
    <div className="w-full">
      <div className="px-4 mt-8 mb-10 xl:px-6">
        <div className="mx-0 xl:mx-6">
          {/* Back Button */}
          <div className="flex items-center justify-between">
            <Link className="flex items-center gap-3 group" to='/learn-the-faith'>
              <div className="flex items-center justify-center rounded-full bg-gold-50 md:h-11 md:w-11 h-9 w-9">
                <LeftArrowIcon className="text-gray-700 group-hover:text-gold-500" />
              </div>
              <h5 className="font-normal text-base leading-5.5 text-gray-600 group-hover:text-gold-500">
                Back to Learn the Faith
              </h5>
            </Link>
            <div className="flex gap-2">
              {/* <a href="#" className="flex items-center justify-center rounded-full bg-gold-50 md:h-11 md:w-11 h-9 w-9 group">
                <HeartIcon className="text-gray-700 w-7 h-7 group-hover:text-gold-500" />
              </a> */}
              <button
                type='button'
                className="flex items-center justify-center rounded-full bg-gold-50 md:h-11 md:w-11 h-9 w-9 group focus:outline-none"
                onClick={(e) => courseData?.id && handleToggleCourseFavorite(e, courseData.id)}
                aria-label="Toggle Course Favorite"
                disabled={!courseData?.id}
              >
                <FavoritesIcon
                  filled={courseData?.favorite}
                  className={`w-7 h-7 group ${courseData?.favorite ? 'text-red-100' : 'text-gray-700'}`}
                />
              </button>
              {/* <a href="#" className="flex items-center justify-center rounded-full bg-gold-50 md:h-11 md:w-11 h-9 w-9 group">
                <LinkIcon className="text-gray-700 group-hover:text-gold-500" />
              </a> */}
              <a
                href="#"
                className="flex items-center justify-center rounded-full bg-gold-50 md:h-11 md:w-11 h-9 w-9 group"
                onClick={(e) => {
                  e.preventDefault(); // Prevent navigation
                  copyToClipboard();
                }}
              >
                <LinkIcon className={`text-gray-700 group-hover:text-gold-500 ${copied ? "text-green-500 group-hover:text-green-500" : ""}`} />
              </a>
            </div>
          </div>

          {/* Course Details (Skeleton if loading) */}
          <div className="mt-4 md:mt-8">
            <div className="flex flex-col xl:flex-row bg-gold-50 rounded-xl xl:gap-8 gap-27">
              <div className="w-full h-64 max-w-full rounded-xl xl:max-w-96">
                {loadingCourse ? (
                  <Skeleton className="h-64 w-full rounded-xl xl:max-w-96" />
                ) : (
                  <img
                    src={courseData?.imageUrl}
                    alt={courseData?.name}
                    className="object-cover w-full h-full rounded-xl"
                  />
                )}
              </div>
              <div className="flex flex-col justify-center px-4 xl:px-0">
                {loadingCourse ? (
                  <>
                    <Skeleton className="h-64 w-full rounded-xl" />
                  </>
                ) : (
                  <>
                    <h4 className="font-bold text-2xl leading-7.5 mb-3">{courseData?.name}</h4>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {/* <span className="px-2 py-1 text-xs font-medium leading-4 text-gold-500 bg-white-50 rounded-27">Tradivox Shorts</span>
                      <span className="px-2 py-1 text-xs font-medium leading-4 text-gold-500 bg-white-50 rounded-27">Easter</span> */}
                    </div>
                    {/* {courseData?.description && ( */}
                    <p className="mb-6 text-lg font-normal leading-7 text-gray-800">{courseData?.description}</p>
                    {/* )} */}
                    <div className="flex flex-row gap-6 mb-8">
                      <div className="flex items-center gap-1">
                        <DocumentIcon className="text-gray-600" />
                        <p className="text-sm font-medium leading-4.5 text-gray-600">{courseData?.count} catechesis</p>
                      </div>
                      <div className="flex items-center gap-1">
                        {courseData?.aboutHours && (
                          <>
                            <MusicCircleicon className="text-gray-600" />
                            <p className="text-xs font-normal leading-4 text-gray-600">
                              {courseData?.aboutHours}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Catechesis List (Skeleton if loading) */}
          {/* Only render list if courseData.count > 0 */}
          {courseData?.count > 0 && (
            loadingList ? (
              <div className="mt-4">
                {Array.from({ length: itemsPerPage }).map((_, index) => (
                  <Skeleton key={index} className="h-24 w-full mb-2 rounded-lg" />
                ))}
              </div>
            ) : catechesisList.length > 0 ? (
              <>
                <ul className="mt-4">
                  {catechesisList.map((lesson, index) => (
                    <li key={lesson.id} className="border-b border-gray-100 flex">
                      <Link className="flex flex-row gap-3 py-4" to={`/learn-the-faith/catechesis/${lesson.slug}`}>
                        <div className="flex justify-center items-start">
                          <p className="text-sm font-medium leading-4.5 text-gray-400">{index + 1}</p>
                        </div>
                        <div className="w-full flex flex-col gap-2">
                          <h5 className="font-semibold font-base leading-5 text-gray-900">{lesson.title}</h5>
                          {lesson.excerpt && (
                            <p
                              className="font-normal text-sm leading-4.5 text-gray-600"
                              dangerouslySetInnerHTML={{ __html: lesson.excerpt }}
                            />
                          )}
                        </div>
                      </Link>
                      <button
                        className="focus:outline-none flex items-center"
                        onClick={(e) => handleToggleFavorite(lesson.id, e)}
                      >
                        <FavoritesIcon
                          filled={lesson.favorite}
                          className={`h-6 w-6 hover:text-red-100 ${lesson.favorite ? 'text-red-100' : 'text-gray-500'}`}
                        />
                      </button>
                    </li>
                  ))}
                </ul>

                {hasMore && catechesisList.length < totalRecords && (
                  <div className="flex justify-center mt-6">
                    <LoadMoreButton
                      label="Load More"
                      onClick={handleLoadMore}
                      isLoading={loadingMore}
                      disabled={loadingMore}
                    />
                  </div>
                )}
              </>
            ) : (!dataLoaded && finalError) ? (
              <div className="text-center text-red-500 mt-4">{finalError}</div>
            ) : (
              <div className="mt-4 text-gray-500 text-center text-lg">
                No catechesis available for this course.
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleLearnTheFaith;
